import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { SHA256 } from 'crypto-js';
import { LocationType } from 'flow/types/Location';

import { setPreventUnload } from 'flow/actions/app-actions';
import { joinMeeting } from 'flow/actions/meeting-actions';
import {
    fetchLobbyData,
    setLobbyChatSessionIdAction,
    setLobbyHostPresentAction,
    setLobbyRequireHostPresentAction,
    setLobbyVideoActiveAction,
    setLobbyViewerIsMeetingHostAction,
    stopLobbyVideoStream,
    updateMeetingAccess,
    updateRequireHostPresent,
} from 'flow/actions/meeting-lobby-actions';
import useAppContext from 'flow/hooks/useAppContext';
import InfoBox, { InfoBoxType } from 'flow/components/ui-components/InfoBox';
import Divider from 'flow/components/ui-components/Divider';
import Page from 'flow/components/Setting/Page';
import Button from 'flow/components/ui-components/Button';
import IconButton from 'flow/components/ui-components/IconButton';
import {
    getOwnCountryCodeOrNull,
    isRetailer,
    selectNameInApp,
} from 'flow/selectors/selectors';
import {
    selectOwnExtensionKey,
    selectOwnUserHasCapability,
} from 'flow/selectors/ExtensionSelectors';
import {
    selectSenderName,
    selectAllMeetingNumbersSorted,
    selectLobbyHostGateClosed,
    selectLobbyMeetingJoinBlockedReason,
    selectLobbyViewerIsMeetingHost,
    selectViewerIsChatSessionAdmin,
} from 'flow/selectors/meeting-selectors';

import { parseUrlParameter } from 'flow/utils/browser-utils';
import {
    getConferenceLink,
    getIfValidBrowser,
    getLoginLink,
    openMeetingLobbyInApplicationFromBrowser,
    parseGetParams,
    shouldShowOpenInAppModal,
    shouldAutoOpenInApp,
    stripSenderNameUuidPrefix,
} from 'flow/utils/meeting-utils';
import BookMeeting from 'flow/components/BookMeeting';
import useFeatureFlags, { FeatureFlags } from 'flow/hooks/useFeatureFlags';
import MeetingLobbyJoiningInfoButtons from './MeetingLobbyJoiningInfoButtons';
import MeetingLobbyJoiningInfoModal from './MeetingLobbyJoiningInfoModal';
import MeetingLobbyJoinOnAnotherDeviceModal from './MeetingLobbyJoinOnAnotherDeviceModal';
import MeetingLobbyOngoingStatus from './MeetingLobbyOngoingStatus';
import MeetingLobbyNameEntry from './MeetingLobbyNameEntry';
import useViewerMeetingRole from 'flow/hooks/useViewerMeetingRole';
import MeetingLobbyVideo from './MeetingLobbyVideo';
import MeetingLobbyOpenInAppDialog from '../../dialogs/MeetingLobbyOpenInAppDialog';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { Retailers } from 'flow/utils/retailer-utils';
import {
    FREE_PRO_NATIONAL_CONFERENCE_NUMBER,
    MeetingAccess,
    MeetingJoinBlockedReason,
} from 'flow/utils/meeting-constants';
import {
    ChatSessionKey,
    ExtensionKey,
    UserConferenceKey,
} from 'flow/types/EntityKeys';
import { roomTypes } from 'flow/utils/message-utils-TW';
import { useModal } from 'flow/contexts/ModalProvider';
import { Capabilities } from 'flow/utils/capability-utils';

const LOBBY_POLL_INTERVAL_MS = 10000;

// An external guest (no extension of their own) can never join a room whose
// access isn't "Anyone with the link". Checked against the known restrictive
// keys explicitly, not by inequality against ANYONE_WITH_LINK — a value this
// client doesn't recognise (a future enum member, a differently-cased
// server value) must not lock every external guest out of every meeting.
const RESTRICTIVE_MEETING_ACCESS_KEYS: string[] = [
    MeetingAccess.ANYONE_IN_ORGANISATION.key,
    MeetingAccess.MEMBERS_ONLY.key,
    MeetingAccess.NO_ONE.key,
];

const isExternalGuestBlockedByMeetingAccess = (
    ownExtensionKey: ExtensionKey | undefined,
    meetingAccess: string | null | undefined,
) =>
    !ownExtensionKey &&
    !!meetingAccess &&
    RESTRICTIVE_MEETING_ACCESS_KEYS.includes(meetingAccess);

interface Props {
    meetingLobbyKey?: ChatSessionKey | UserConferenceKey;
    onClickBack?: MouseEventHandler;
    allowToggleNewMembers?: boolean;
    // Only ever true from PbxConference, the sole route that reaches this
    // component with a UserConferenceKey (the Personal Meeting Room) — kept
    // as an explicit prop rather than re-derived from meetingLobbyKey's
    // shape, since the caller already knows it statically.
    isPersonalMeetingRoom?: boolean;
}

const MeetingLobby = ({
    meetingLobbyKey,
    onClickBack,
    allowToggleNewMembers,
    isPersonalMeetingRoom = false,
}: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    // Carried from TodayMeetingItem's history.push when opening a scheduled
    // meeting from Today's Meetings (VID-436) — absent for every other way
    // of reaching the lobby (Personal Meeting Room, instant meeting, a
    // plain join link), which is exactly when they should be undefined.
    const { scheduledStart, scheduledEnd } = history.location.state ?? {};
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const { hasFeature } = useFeatureFlags();

    const ownUserHasCapabilityVideo = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.VIDEO),
    );
    const ownUserHasCapabilityVideo1on1 = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.VIDEO_1_ON_1),
    );

    // The modal context isn't available when logged-out users join through a link.
    // In that case, we manually mount the modal as a fallback.
    const { openModal } = useModal();

    const [showStandaloneModal, setShowStandaloneModal] = useState(false);
    const [showJoiningInfoModal, setShowJoiningInfoModal] = useState(false);
    const [showJoinOnAnotherDeviceModal, setShowJoinOnAnotherDeviceModal] =
        useState(false);

    const nameInApp = useAppSelector(selectNameInApp);
    const senderName = useAppSelector(selectSenderName);

    const session = useAppSelector((state) =>
        state?.chats?.getIn(['sessions', meetingLobbyKey]),
    );

    const isJoiningThroughLink = !meetingLobbyKey;

    // `!ownExtensionKey` alone covers logged-out link-joiners (they have no
    // capability to check, so the video UI is their only way in); a
    // `!meetingLobbyKey` (link-join) shortcut here would also force video on
    // for logged-in non-video-capable users following a lobby-key-less link
    // (e.g. an internal calendar meeting), which is the AC-1570 bug.
    const shouldShowVideo =
        ownUserHasCapabilityVideo ||
        (ownUserHasCapabilityVideo1on1 &&
            session?.get('roomType') === roomTypes.session &&
            session?.get('members')?.size <= 2) ||
        !ownExtensionKey ||
        !nameInApp;

    const lobbyPin = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'pin']),
    );
    const lobbyNumber = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'number']),
    );
    const meetingCode = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'meetingCode']),
    );
    const meetingAccess = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'meetingAccess']),
    );
    const requireHostPresent = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'requireHostPresent']),
    );
    const roomNotJoinedRoomName = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'roomNotJoined', 'roomName']),
    );
    const roomNotJoinedCause = useAppSelector((state) =>
        state.meeting.getIn(['lobby', 'roomNotJoined', 'cause']),
    );
    const isRetailerFreePro = useAppSelector((state) =>
        isRetailer(state, Retailers.FREEPRO),
    );
    const ownCountryCode = useAppSelector(getOwnCountryCodeOrNull);
    const meetingNumbers = useAppSelector(selectAllMeetingNumbersSorted);
    const meetingJoinBlockedReason = useAppSelector(
        selectLobbyMeetingJoinBlockedReason,
    );
    const viewerIsMeetingHost = useAppSelector(selectLobbyViewerIsMeetingHost);
    // Any non-NONE reason means no access; the host is exempt (VID-309).
    const showMeetingNoAccessMessage =
        !viewerIsMeetingHost &&
        !!meetingJoinBlockedReason &&
        meetingJoinBlockedReason !== MeetingJoinBlockedReason.NONE.key;
    const hostGateClosed = useAppSelector(selectLobbyHostGateClosed);

    // Rooms have no single host, so meeting access there is a room-admin
    // affordance rather than the personal-conference owner's (allowToggleNewMembers).
    const viewerIsRoomAdmin = useAppSelector((state) =>
        selectViewerIsChatSessionAdmin(
            state,
            meetingLobbyKey as ChatSessionKey,
        ),
    );
    const showMeetingAccessSection = allowToggleNewMembers || viewerIsRoomAdmin;

    const getMobileConferenceLink = useCallback(() => {
        const countryCode =
            (IS_UAE_HOST && 'ae') ||
            ownCountryCode?.toLowerCase() ||
            parseUrlParameter('countryCode') ||
            meetingNumbers?.values().next().value?.countryCode ||
            null;
        return getConferenceLink(meetingCode, countryCode);
    }, [meetingCode, ownCountryCode, meetingNumbers]);

    const handleOpenInApp = () => {
        setPreventUnload(true);
        openMeetingLobbyInApplicationFromBrowser(meetingCode);
        setTimeout(() => {
            setShowStandaloneModal(false);
        }, 100);
    };

    const handleStayInBrowser = () => {
        setShowStandaloneModal(false);
    };

    const showOpenInAppModal = () => {
        if (openModal && typeof openModal === 'function') {
            openModal('openMeetingInApp', {
                onConfirm: handleOpenInApp,
                onCancel: handleStayInBrowser,
            });
        } else {
            setShowStandaloneModal(true);
        }
    };

    // Seeded from the name this lobby last joined under, not just the app's
    // own: a guest types a name that lives only in this component, and the
    // lobby can be unmounted and remounted under them — being sent back here
    // after asking to join, most obviously — which would otherwise clear the
    // field and make it look as though they never entered one.
    const [name, setName] = useState(
        nameInApp || stripSenderNameUuidPrefix(senderName) || '',
    );
    const parsedParams = parseGetParams();

    const asyncGetLobbyData = useCallback(
        async (clearStateOnFailure = false) => {
            await dispatch(
                fetchLobbyData(meetingLobbyKey, clearStateOnFailure),
            );
        },
        [dispatch, meetingLobbyKey],
    );

    // The lobby's one and only asking of who this viewer is, handed down to the
    // button rather than asked again there. Two askings are two questions about
    // the same person and the same meeting, with nothing making the answers
    // agree — so the button could end up saying one thing while the join below
    // does another. It also has to be read here for pressing Enter in the name
    // field to start the same kind of join as clicking the button does.
    // Costs a guest nothing: no request is made for someone who is not signed in.
    const viewerMeetingRole = useViewerMeetingRole();
    const { viewerMustAskToJoin, viewerMustAskToJoinIsKnown } =
        viewerMeetingRole;

    // VID-198: the meeting waits for its host and none has arrived. Everyone
    // but the host waits here, a guest included: asking to join (VID-76) is no
    // substitute for waiting, because whoever could admit them is being held
    // at this same gate, so the ask could only ever time out. Once the host is
    // in, a guest's join asks as usual. Someone with no access at all is told
    // that rather than to wait. The lobby's poll picks up the host's arrival,
    // at which point this clears and the join button comes back.
    const waitingForHost = hostGateClosed;
    const showWaitingForHostMessage =
        waitingForHost && !showMeetingNoAccessMessage;

    const joinWithName = useCallback(
        async (username: string) => {
            if (username) {
                await asyncGetLobbyData(true);

                dispatch(
                    joinMeeting(
                        username,
                        undefined,
                        viewerMustAskToJoin && viewerMustAskToJoinIsKnown,
                    ),
                );
            }
        },
        [
            dispatch,
            asyncGetLobbyData,
            viewerMustAskToJoin,
            viewerMustAskToJoinIsKnown,
        ],
    );

    useEffect(() => {
        dispatch(setLobbyVideoActiveAction(false));
        dispatch(stopLobbyVideoStream());
    }, [dispatch]);

    useEffect(() => {
        const joinedWithLink = !!parsedParams?.roomName;

        // None of these fields has a default in the reducer, so they would
        // otherwise keep the previous lobby's answer (e.g. a personal
        // room's chatSessionId: null, viewerIsMeetingHost: true) while this
        // lobby's own fetch is in flight — letting regenerate-link render
        // under the old lobby's identity for a moment (AC-1800 review), or
        // the host gate close on the old lobby's requireHostPresent /
        // hostPresent (VID-198 review). Null reads as "unknown", which the
        // gate treats as open, so nothing is shown until this lobby answers.
        dispatch(setLobbyChatSessionIdAction(undefined));
        dispatch(setLobbyViewerIsMeetingHostAction(null));
        dispatch(setLobbyRequireHostPresentAction(null));
        dispatch(setLobbyHostPresentAction(null));

        asyncGetLobbyData(joinedWithLink);

        const pollingInterval = setInterval(
            () => asyncGetLobbyData(joinedWithLink),
            LOBBY_POLL_INTERVAL_MS,
        );

        return () => {
            clearInterval(pollingInterval);
        };
    }, [asyncGetLobbyData, dispatch, parsedParams.roomName]);

    // meetingCode starts `undefined` and is only ever set (to a code, or to
    // `null` for an invalid link) once fetchLobbyData's first response lands
    // — the same signal the hard block below waits for, so autoJoin can't
    // fire on a still-`null` meetingAccess before the block has had a chance
    // to apply.
    const lobbyDataLoaded = meetingCode !== undefined;

    useEffect(() => {
        if (
            parsedParams?.autoJoin === '1' &&
            parsedParams?.userName &&
            lobbyDataLoaded &&
            !isExternalGuestBlockedByMeetingAccess(
                ownExtensionKey,
                meetingAccess,
            ) &&
            // Held until the host arrives; the poll flips this and the join
            // then goes ahead on its own, as an auto-join link promises.
            !waitingForHost
        ) {
            joinWithName(parsedParams.userName);
        }
    }, [
        parsedParams,
        joinWithName,
        lobbyDataLoaded,
        ownExtensionKey,
        meetingAccess,
        waitingForHost,
    ]);

    useEffect(() => {
        if (!meetingCode) {
            return;
        }

        if (shouldAutoOpenInApp(isJoiningThroughLink)) {
            handleOpenInApp();
        } else if (shouldShowOpenInAppModal(isJoiningThroughLink)) {
            showOpenInAppModal();
        }
    }, [meetingCode]);

    if (meetingCode === null) {
        return (
            <div className="flex size-full flex-col items-center justify-center">
                <h1 className="text-2xl font-bold">
                    {i18next.t('videoconference.invalid_video_link_title')}
                </h1>
                <div>
                    {i18next.t(
                        'videoconference.invalid_video_link_description',
                    )}
                </div>
            </div>
        );
    }

    // Hard-blocked before any lobby chrome (video preview, name entry, join
    // button) even renders.
    if (isExternalGuestBlockedByMeetingAccess(ownExtensionKey, meetingAccess)) {
        return (
            <div className="flex size-full flex-col items-center justify-center p-6">
                <div className="flex w-full max-w-sm items-center justify-center rounded-sm bg-gray-200 p-8 text-center text-gray-800">
                    {i18next.t('videoconference.lobby.not_available')}
                </div>
            </div>
        );
    }

    const renderLobbyHeaderAction = () => {
        // AC-1839: "Schedule in calendar" only makes sense for the Personal
        // Meeting Room — its persistent link is worth adding to a calendar;
        // a one-off room-for-later/instant-meeting lobby has no such use.
        if (
            isPersonalMeetingRoom &&
            ownExtensionKey &&
            shouldShowVideo &&
            hasFeature(FeatureFlags.VIDEO_CANVAS)
        ) {
            return <BookMeeting link={getMobileConferenceLink()} />;
        }
        if (!ownExtensionKey) {
            return (
                <IconButton
                    onClick={() => {
                        window.location.href = getLoginLink(meetingCode);
                    }}
                >
                    {i18next.t('videoconference.lobby.sign_in')}
                </IconButton>
            );
        }
        return null;
    };

    return (
        <Page
            header={
                isPersonalMeetingRoom
                    ? 'videoconference.lobby.personal_conference_lobby'
                    : 'videoconference.lobby.conference_lobby'
            }
            className="min-h-0 flex-1 gap-y-6"
            mainClassName="scrollbar-gutter-stable-both"
            onClickBack={onClickBack}
            action={renderLobbyHeaderAction()}
        >
            {shouldShowVideo && <MeetingLobbyVideo />}

            {/* The row you act on. A colleague gets the joining info and the
                other-device link beside the join button (VID-429); a guest gets
                the name field and the button alone, with the other-device link
                below — they have one thing to do here, and the row says so. */}
            {(!!ownExtensionKey || shouldShowVideo) && (
                <div className="flex items-center gap-3">
                    {!!ownExtensionKey && (
                        <MeetingLobbyJoiningInfoButtons
                            onShowJoiningInfo={() =>
                                setShowJoiningInfoModal(true)
                            }
                            onShowJoinOnAnotherDevice={() =>
                                setShowJoinOnAnotherDeviceModal(true)
                            }
                            showJoinOnAnotherDevice={shouldShowVideo}
                            joinOnAnotherDeviceDisabled={
                                showMeetingNoAccessMessage
                            }
                        />
                    )}

                    {shouldShowVideo && (
                        <MeetingLobbyNameEntry
                            className="flex-1"
                            name={name}
                            onChangeName={setName}
                            onJoinMeeting={() => joinWithName(name)}
                            // A colleague already has a name and is not asked
                            // for one. The fallback is for the case that would
                            // otherwise be unrecoverable: signed in, but with
                            // no name to join under and no field to supply one,
                            // which the join button would refuse forever.
                            showNameInput={!ownExtensionKey || !name}
                            viewerMeetingRole={viewerMeetingRole}
                        />
                    )}
                </div>
            )}

            {showMeetingNoAccessMessage && (
                <>
                    <Divider className="my-0" />
                    <div className="text-sm text-gray-800">
                        {i18next.t('videoconference.lobby.no_access')}
                    </div>
                </>
            )}

            {showWaitingForHostMessage && (
                <>
                    <Divider className="my-0" />
                    <div
                        className="text-sm text-gray-800"
                        data-test-id="meeting-waiting-for-host"
                    >
                        {i18next.t('videoconference.lobby.waiting_for_host')}
                    </div>
                </>
            )}

            {/* Who is already in the meeting reads as a state of the world,
                not as something to act on, so it sits under the action row
                rather than in it. Renders nothing until somebody is there. */}
            <MeetingLobbyOngoingStatus
                scheduledStart={scheduledStart}
                scheduledEnd={scheduledEnd}
            />

            {shouldShowVideo && !getIfValidBrowser() && (
                <InfoBox
                    type={InfoBoxType.Alert}
                    linkText={i18next.t('videoconference.lobby.use_app')}
                    onClickLink={() => {
                        setPreventUnload(true);
                        openMeetingLobbyInApplicationFromBrowser(meetingCode);
                    }}
                >
                    {i18next.t('videoconference.lobby.support_browser', {
                        browser1: 'Firefox',
                        browser2: 'Chrome',
                    })}
                </InfoBox>
            )}

            {roomNotJoinedRoomName === SHA256(meetingCode).toString() ? (
                <InfoBox type={InfoBoxType.Alert}>
                    {i18next.t(roomNotJoinedCause)}
                </InfoBox>
            ) : null}

            {!ownExtensionKey && shouldShowVideo && (
                <div className="flex justify-center">
                    <Button
                        color="ghost"
                        className="h-10 rounded-2xl px-4 text-gray-800 ring-gray-300"
                        onClick={() => setShowJoinOnAnotherDeviceModal(true)}
                        disabled={showMeetingNoAccessMessage}
                        data-test-id="lobby-join-on-another-device"
                    >
                        {i18next.t(
                            'videoconference.lobby.join_on_another_device',
                        )}
                    </Button>
                </div>
            )}

            {showStandaloneModal && (
                <MeetingLobbyOpenInAppDialog
                    onConfirm={handleOpenInApp}
                    onCancel={handleStayInBrowser}
                />
            )}

            {showJoiningInfoModal && (
                <MeetingLobbyJoiningInfoModal
                    meetingCode={meetingCode}
                    number={
                        isRetailerFreePro
                            ? FREE_PRO_NATIONAL_CONFERENCE_NUMBER
                            : lobbyNumber
                    }
                    pinCode={lobbyPin}
                    shouldShowVideo={shouldShowVideo}
                    allowToggleNewMembers={showMeetingAccessSection}
                    meetingAccess={meetingAccess}
                    onChangeMeetingAccess={(newAccess) =>
                        dispatch(
                            updateMeetingAccess(meetingLobbyKey, newAccess),
                        )
                    }
                    requireHostPresent={requireHostPresent}
                    onChangeRequireHostPresent={(newRequireHostPresent) =>
                        dispatch(
                            updateRequireHostPresent(
                                meetingLobbyKey,
                                newRequireHostPresent,
                            ),
                        )
                    }
                    onClose={() => setShowJoiningInfoModal(false)}
                />
            )}

            {showJoinOnAnotherDeviceModal && (
                <MeetingLobbyJoinOnAnotherDeviceModal
                    meetingCode={meetingCode}
                    dialInNumber={
                        isRetailerFreePro
                            ? FREE_PRO_NATIONAL_CONFERENCE_NUMBER
                            : lobbyNumber
                    }
                    pin={lobbyPin}
                    conferenceLink={getMobileConferenceLink()}
                    onClose={() => setShowJoinOnAnotherDeviceModal(false)}
                />
            )}
        </Page>
    );
};

export default MeetingLobby;
